import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { Int_ApplicationDetails, Int_User, Int_UserApplications, MobiOfficeUIState } from "./mobi-office-ui.interface";
import {
  GET_MOBIOFFICE_UI_ApplicationDetails,
  GET_MOBIOFFICE_UI_ApplicationDetails_SUCCESS,
  GET_MOBIOFFICE_UI_DynamicTableColumn,
  GET_MOBIOFFICE_UI_DynamicTableColumn_SUCCESS,
  GET_MOBIOFFICE_UI_User,
  GET_MOBIOFFICE_UI_UserApplications,
  GET_MOBIOFFICE_UI_UserApplications_SUCCESS,
  GET_MOBIOFFICE_UI_User_SUCCESS,
  dummyAction,
} from "./mobi-office-ui.action";
import { select_MOUI_ApplicationDetails, select_MOUI_DynamicTableColumn, select_MOUI_User, select_MOUI_UserApplications } from "./mobi-office-ui.selector";
import { EMPTY, catchError, map, mergeMap, of, withLatestFrom } from "rxjs";
import { UserManagementService } from "../services/user-management.service";
import { ENVIRONMENT } from "../environments/environments.token";
import { Environment } from "../environments/environment.interface";

@Injectable()
export class MobiOfficeUIEffect {
  private readonly store = inject<Store<MobiOfficeUIState>>(Store);
  private readonly action$ = inject(Actions);
  private readonly userManagementService = inject(UserManagementService);
  private readonly env = inject<Environment>(ENVIRONMENT);

  constructor() {}

  loadUserLoggedInInfo$ = createEffect(() =>
    this.action$.pipe(
      ofType(GET_MOBIOFFICE_UI_User),
      withLatestFrom(this.store.select(select_MOUI_User)),
      mergeMap(([action, currentState]) => {
        return this.userManagementService.UserLogInUsingToken().pipe(
          map((res: any) => {
            this.userManagementService.loginObject(res);
            const tempModal: Int_User = {
              UserInfo: JSON.parse(JSON.stringify(res)),
            };
            return GET_MOBIOFFICE_UI_User_SUCCESS({ payload: tempModal });
          })
        );
      })
    )
  );

  loadTableColumn$ = createEffect(() =>
    this.action$.pipe(
      ofType(GET_MOBIOFFICE_UI_DynamicTableColumn),
      withLatestFrom(this.store.select(select_MOUI_DynamicTableColumn)),
      mergeMap(([action, currentState]) => {
        const state = JSON.parse(JSON.stringify(currentState));
        if ((state[action.tableName] && state.IsLoaded) || !this.env.APP_NAME) return of(dummyAction());
        return this.userManagementService.GetTableColumnsByName(action.tableName, 0, this.env.APP_NAME).pipe(
          map((res: any) => {
            const { Table } = res;
            const currentResult = Table;
            const currentTableColumn: any = {};
            currentTableColumn[currentResult.TableName] = {
              defaultTableColumn: currentResult?.DefaultName,
              TableColumns: currentResult?.MTC_TCList,
            };
            currentTableColumn.IsLoaded = true;
            return GET_MOBIOFFICE_UI_DynamicTableColumn_SUCCESS({ payload: currentTableColumn });
          })
        );
      }),
      catchError(() => EMPTY)
    )
  );

  loadApplicationDetails$ = createEffect(() =>
    this.action$.pipe(
      ofType(GET_MOBIOFFICE_UI_ApplicationDetails),
      withLatestFrom(this.store.select(select_MOUI_ApplicationDetails)),
      mergeMap(([action, currentState]) => {
        const state = JSON.parse(JSON.stringify(currentState));
        if (state.IsLoaded || !this.env.APP_NAME) return of(dummyAction());
        return this.userManagementService.GetApplicationByAID(0, this.env.APP_NAME).pipe(
          map((res: any) => {
            const { Table } = res;
            const root = document.documentElement;
            const { A_SVList } = Table;
            if (A_SVList?.length) {
              const defaultTheme = this.env.DEFAULT_THEME || "dark";
              A_SVList.filter((el: any) => !el.Theme || el.Theme === defaultTheme).forEach((element: any) => {
                root?.style.setProperty(element.Name, element.Value, "important");
              });

              // set theme on head dark or light
              root.setAttribute("theme", defaultTheme);
            }

            const tempModal: Int_ApplicationDetails = {
              IsLoaded: true,
              ApplicationDetails: Table,
            };
            return GET_MOBIOFFICE_UI_ApplicationDetails_SUCCESS({ payload: tempModal });
          })
        );
      }),
      catchError(() => EMPTY)
    )
  );

  loadUserApplication$ = createEffect(() =>
    this.action$.pipe(
      ofType(GET_MOBIOFFICE_UI_UserApplications),
      withLatestFrom(this.store.select(select_MOUI_UserApplications)),
      mergeMap(([action, currentState]) => {
        const state = JSON.parse(JSON.stringify(currentState));
        if (state.IsLoaded || !this.env.APP_NAME) return of(dummyAction());
        return this.userManagementService.GetUserApplicationsByUID().pipe(
          map((res: any) => {
            const { Table, Table1 } = res;
            Table.map((el: any) => {
              el.RolesList = Table1?.filter((i: any) => +i.AID === +el.AID);
            });
            const tempModal: Int_UserApplications = {
              IsLoaded: true,
              ApplicationsList: Table,
            };
            return GET_MOBIOFFICE_UI_UserApplications_SUCCESS({ payload: tempModal });
          })
        );
      }),
      catchError(() => EMPTY)
    )
  );
}
